import { EliteYearlyUpgradeBottomSheet } from '../../BottomSheetComponents/EliteYearlyUpgradeBottomSheet'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import type { EliteOnboardingStatusResponse } from '../../../types/misc.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { FamilyOnboardingViewProps } from '../../../features/new-elite-landing/pages/family/familyOnboardingViewProps'
import NewEliteFamilyPrimaryNonEliteOfferCard from '../../../features/new-elite-landing/components/family/NewEliteFamilyPrimaryNonEliteOfferCard'
import { useNewEliteLandingAnalyticsScreen } from '../../../features/new-elite-landing/analytics/NewEliteLandingAnalyticsContext'
import {
  NewEliteLandingBottomSheetName,
  NewEliteLandingEventId,
  trackNewEliteUserClicked
} from '../../../features/new-elite-landing/analytics/newEliteLandingAnalytics'

import { getEliteBuyCtaEventExtras } from '../../../utils/eliteEventExtras'
export function EliteMembershipUpgradeSection({
  eliteOnboardingStatus,
  familyConfig
}: {
  eliteOnboardingStatus?: EliteOnboardingStatusResponse
  familyConfig?: FamilyOnboardingViewProps
}) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const upgradePlan = eliteOnboardingStatus?.subscriptionDetails?.upgradeDetails?.upgradePlanDetails
  if (!upgradePlan) {
    return null
  }

  if (familyConfig?.isEliteBlockedBecausePrimaryNotElite) {
    return (
      <div className="-mx-5">
        <NewEliteFamilyPrimaryNonEliteOfferCard
          familyMemberDisplayName={familyConfig.secondaryUserName}
          eligiblePlans={eliteOnboardingStatus?.eligiblePlans ?? []}
        />
      </div>
    )
  }

  const upgradePlanTenureLabel = getPlanTenureLabel({
    period: upgradePlan?.effectivePlanTenure?.period ?? upgradePlan?.basePlanTenure?.period,
    value: Number(upgradePlan?.effectivePlanTenure?.value ?? upgradePlan?.basePlanTenure?.value ?? 0)
  })
  const upgradePlanSuffix = upgradePlanTenureLabel === 'Yearly' ? '/yr' : '/mo'
  const effectiveFeesExGst = upgradePlan?.effectivePlanFees?.amount
  const actualListPriceExGst = upgradePlan?.actualPlanPrice?.amount
  const hasEffectiveFees = effectiveFeesExGst != null
  const showStrikethroughListPrice =
    hasEffectiveFees &&
    actualListPriceExGst != null &&
    actualListPriceExGst > (effectiveFeesExGst ?? 0)
  const { percentageSavings } = eliteOnboardingStatus?.subscriptionDetails?.upgradeDetails?.upgradePricingDetails ?? {}

  const openUpgradeBottomSheet = () => {
    trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.UpgradeOpenSheet, {
      ...getEliteBuyCtaEventExtras(eliteOnboardingStatus),
      bottomSheetName: NewEliteLandingBottomSheetName.EliteYearlyUpgrade
    })
    openBottomSheet(
      <EliteYearlyUpgradeBottomSheet
        familyConfig={familyConfig}
        eliteOnboardingStatus={eliteOnboardingStatus}
        onDismiss={closeBottomSheet}
      />
    )
  }

  return (
    <div className='relative z-10 overflow-hidden rounded-xl bg-gradient-to-r from-[#CC5C2D] to-[#D16B3F] text-center text-[13px] text-white'>
      {familyConfig?.isFamilyMode ? (
        <div className='px-4 py-2.5 font-medium leading-[1.3]'>
          <p className='text-[11px] leading-[1.2] tracking-[0.04em]'>{familyConfig.familyOrangeStripText?.headline}</p>
          <p className='mt-1 text-[13px] leading-[1.4] font-medium'>
            {familyConfig.familyOrangeStripText?.subline}
          </p>
        </div>
      ) : (
        <div className='px-4 py-2.5 font-medium leading-[1.3]'>
          <span className='font-semibold'>
            SAVE {(percentageSavings ?? 0).toFixed(0)}%
          </span>
          {' '}by switching to Yearly plan
        </div>
      )}
      <div className='rounded-xl bg-white px-4 py-[14px] text-left'>
        <div className='flex items-center justify-between gap-3'>
          <div>
            <p className='text-sm font-medium text-[#65321D]'>Switch to Elite {upgradePlanTenureLabel}</p>
            <div
              onClick={openUpgradeBottomSheet}
              className='cursor-pointer text-[13px] font-medium text-[#BB4F22] underline'
            >
              View breakup
            </div>
          </div>
          <div className='text-right'>
            <p className='text-sm font-medium text-[#232323]'>
              {roundOffAndFormatNumberWithCurrency(upgradePlan?.effectivePlanFees?.amount ?? 0)}
              {upgradePlanSuffix}
              <span className='text-[12px] font-normal text-[#8E9393]'>+GST</span>
            </p>
            {showStrikethroughListPrice ? (
              <p className='text-[13px] text-[#8E9393] line-through'>
                {roundOffAndFormatNumberWithCurrency(actualListPriceExGst)}
                {upgradePlanSuffix}
              </p>
            ) : null}
          </div>
        </div>

        <div
          onClick={openUpgradeBottomSheet}
          className='mt-[18px] flex cursor-pointer items-center justify-center rounded-xl bg-[#232323] py-4 text-base font-medium text-white'
        >
          Switch to Elite {upgradePlanTenureLabel}
        </div>
      </div>
    </div>
  )
}

function getPlanTenureLabel({ period, value }: { period?: string; value: number }) {
  if (period === 'YEAR' || value >= 12) {
    return 'Yearly'
  }
  return 'Monthly'
}
